/**
 * ShootAction - behavior tree action for ranged combat.
 */

import * as THREE from 'three';

import { ProjectileWeaponComponent } from '../../../../../components/gameplay/ProjectileWeaponComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface ShootActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  shootRange?: number;
  shootCooldown?: number;
  aimDuration?: number;
}

@EngineClass('ShootAction')
export class ShootAction extends BehaviorAction {
  private targetActorKey?: string;
  private shootRange: number;
  private shootCooldown: number;
  private aimDuration: number;
  private projectileWeapon: ProjectileWeaponComponent | null = null;
  private lastShootTime: number = 0;
  private isAiming: boolean = false;
  private aimStartTime: number = 0;

  constructor(options: ShootActionOptions = {}) {
    super({
      name: 'Shoot',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.shootRange = options.shootRange ?? 20.0;
    this.shootCooldown = options.shootCooldown ?? 1.5;
    this.aimDuration = options.aimDuration ?? 0.5;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    // Find ProjectileWeaponComponent - it's attached to the root component
    const rootComponent = owner.rootComponent;
    rootComponent.traverse((child) => {
      if (child instanceof ProjectileWeaponComponent) {
        this.projectileWeapon = child;
      }
    });

    if (!this.projectileWeapon) {
      console.warn('ShootAction: No ProjectileWeaponComponent found on actor');
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    this.isAiming = false;
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return BehaviorStatus.Failure;
    }

    // Get target
    const target = this.getTarget(blackboard);
    if (!target) {
      return BehaviorStatus.Failure;
    }

    // Get positions
    const ownerPosition = new THREE.Vector3();
    const targetPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    target.rootComponent.getWorldPosition(targetPosition);

    // Calculate distance
    const distance = ownerPosition.distanceTo(targetPosition);

    // Check if target is in range
    if (distance > this.shootRange) {
      return BehaviorStatus.Failure;
    }

    const currentTime = Date.now() / 1000;

    // Check cooldown
    if (currentTime - this.lastShootTime < this.shootCooldown) {
      return BehaviorStatus.Running;
    }

    // Check if we're currently aiming
    if (this.isAiming) {
      const aimElapsed = currentTime - this.aimStartTime;
      if (aimElapsed >= this.aimDuration) {
        // Aiming complete, shoot
        this.performShoot(owner, target);
        this.lastShootTime = currentTime;
        this.isAiming = false;
        return BehaviorStatus.Success;
      }
      return BehaviorStatus.Running;
    }

    // Start aiming
    this.startAiming(owner, target);
    this.aimStartTime = currentTime;
    this.isAiming = true;

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    this.isAiming = false;
  }

  private getTarget(blackboard: Blackboard): Actor | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        return actor;
      }
    }
    return null;
  }

  private startAiming(owner: Actor, target: Actor): void {
    // TODO: Add aiming logic, crosshair, etc.
  }

  private performShoot(owner: Actor, target: Actor): void {
    if (!this.projectileWeapon) {
      console.warn('ShootAction: No ProjectileWeaponComponent available for shooting');
      return;
    }

    // Get target position
    const targetPosition = new THREE.Vector3();
    target.rootComponent.getWorldPosition(targetPosition);

    // Fire projectile at target
    this.projectileWeapon.tryFireAtTarget(targetPosition);
  }
}
